import React from 'react';
import { useQuery } from '@apollo/client';
import i18next from 'i18next';
import { PRODUCTDETAIL_LIST_QUERY } from 'pages/content/gql';
import { EContentProductDetailPosition } from '__generated__/graphql';

interface LocalizedSkuTextProps {
  sku: string;
  fallback: string;
  position?: EContentProductDetailPosition;
  hotelId?: number | null;
  spaceId?: number | null;
}

const normalizeLang = (lng?: string | null): string => (lng || 'de').toLowerCase().slice(0, 2);

/**
 * Resolves localized product/service text from content text modules by SKU.
 * Falls back to German/default name when no matching module exists.
 */
export default function LocalizedSkuText({
  sku,
  fallback,
  position = EContentProductDetailPosition.HEADER,
  hotelId = null,
  spaceId = null,
}: LocalizedSkuTextProps) {
  const uiLanguage = normalizeLang(i18next.language);
  const { data } = useQuery(PRODUCTDETAIL_LIST_QUERY, {
    variables: { sku },
    skip: !sku || uiLanguage === 'de',
    fetchPolicy: 'cache-first',
  });

  if (uiLanguage === 'de' || !data?.listContentProductDetails?.productDetails?.length) {
    return <>{fallback}</>;
  }

  const details = data.listContentProductDetails.productDetails.filter(d => d.position === position);
  if (details.length === 0) return <>{fallback}</>;

  const scored = [...details].sort((a, b) => {
    const score = (d: (typeof details)[number]) => {
      let s = 0;
      if (hotelId && d.hotel?.id === hotelId) s += 100;
      if (!d.hotel) s += 20;
      if (spaceId && d.space?.id === spaceId) s += 10;
      return s;
    };
    return score(b) - score(a);
  });

  const pickText = (texts: Array<{ language?: string | null; text?: string | null }>) =>
    texts.find(t => normalizeLang(t.language) === uiLanguage)?.text ||
    texts.find(t => normalizeLang(t.language) === 'en')?.text ||
    texts.find(t => normalizeLang(t.language) === 'de')?.text ||
    texts.find(t => !!t.text)?.text ||
    null;

  const localized = scored.map(d => pickText(d.texts || [])).find(Boolean);
  return <>{localized || fallback}</>;
}
